import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import JSZip from 'jszip';
import {
  Workspace,
  BrandInfo,
  BrandAsset,
  GeneratedPost,
  SocialNetwork,
  SOCIAL_NETWORKS,
  TemplateSummary,
  Template,
} from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Input, InputWrapper, Label, TextArea } from '../components/Input';
import {
  renderCompositeAsBlob,
  renderCompositeThumbnail,
  ExportQuality,
  EXPORT_PRESETS,
} from '../utils/canvasRenderer';
import { NETWORK_ICONS } from '../utils/networkIcons';

// ─── Response types ───

interface ScrapeResponse extends BrandInfo {
  scrapedImages?: string[];
}

interface GenerateResponse {
  posts: GeneratedPost[];
  creditsUsed: number;
  creditsRemaining: number;
}

// ─── Styled components ───

const PageWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const StepperWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
`;

const StepDot = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme, $active, $completed }) =>
    $active ? theme.colors.primary : $completed ? theme.colors.success : theme.colors.surface};
  color: ${({ $active, $completed }) => ($active || $completed ? 'white' : 'inherit')};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.md};
  box-shadow: ${({ theme, $active }) => ($active ? theme.shadows.md : 'none')};
`;

const StepConnector = styled.div<{ $completed: boolean }>`
  width: 40px;
  height: 3px;
  background-color: ${({ theme, $completed }) =>
    $completed ? theme.colors.success : theme.colors.border};
`;

const StepTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StepSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const StepCard = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.medium};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const StepActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  gap: ${({ theme }) => theme.spacing.md};
`;

const BrandSummary = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SummaryField = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: ${({ theme }) => theme.borders.thin};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
`;

const LogoUploadArea = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  cursor: pointer;
  text-align: center;
  min-height: 120px;
  margin-top: ${({ theme }) => theme.spacing.md};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }

  input {
    display: none;
  }
`;

const LogoPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: ${({ theme }) => theme.borders.thin};

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    border: ${({ theme }) => theme.borders.thin};
  }
`;

// Step 2 - Network selector

const NetworkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const NetworkCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background-color: ${({ theme, $active }) => ($active ? theme.colors.warning : theme.colors.surface)};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ theme, $active }) => ($active ? theme.shadows.sm : 'none')};

  &:hover {
    transform: translate(1px, 1px);
  }
`;

const NetworkIcon = styled.span`
  font-size: 24px;
`;

const CountRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const CountLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const CountInput = styled.input`
  width: 80px;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SelectedSummary = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: ${({ theme }) => theme.borders.thin};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

// Step 3 - Loading

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 6px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const LoadingSubtext = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Step 4 - Results

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const TemplateSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  background-color: ${({ theme }) => theme.colors.surface};
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const PostCard = styled.div`
  border: ${({ theme }) => theme.borders.medium};
  background-color: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const PostImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  overflow: hidden;
  position: relative;

  img, canvas {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PostBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`;

const PostNetwork = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PostText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ExistingBrandBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: 2px solid ${({ theme }) => theme.colors.success};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  span {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  max-width: 300px;
  height: 6px;
  background-color: ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background-color: ${({ theme }) => theme.colors.primary};
  transition: width 0.3s ease;
`;

// ─── Component ───

export function QuickGenPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { user, updateCredits } = useAuth();

  // Admin guard
  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Wizard state
  const [step, setStep] = useState(1);

  // Step 1 - Brand
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [brandInfo, setBrandInfo] = useState<BrandInfo | null>(null);
  const [brandAssets, setBrandAssets] = useState<BrandAsset[]>([]);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Step 2 - Config
  const [selectedNetworks, setSelectedNetworks] = useState<SocialNetwork[]>(['instagram', 'facebook']);
  const [postsPerNetwork, setPostsPerNetwork] = useState(3);
  const [customPrompt, setCustomPrompt] = useState('');

  // Step 3/4 - Generation & Results
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [exportQuality, setExportQuality] = useState<ExportQuality>('mobile');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');

  // Load workspace data on mount
  useEffect(() => {
    if (!workspaceId) return;
    const loadWorkspace = async () => {
      try {
        const ws = await apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`);
        setWorkspace(ws);
      } catch {
        navigate('/dashboard');
      }
    };
    loadWorkspace();
  }, [workspaceId, navigate]);

  // Load existing brand info & assets
  useEffect(() => {
    if (!workspaceId) return;
    const loadBrand = async () => {
      try {
        const [info, assets] = await Promise.all([
          apiClient.get<BrandInfo>(`/api/workspaces/${workspaceId}/brand-info`).catch(() => null),
          apiClient.get<BrandAsset[]>(`/api/workspaces/${workspaceId}/assets`).catch(() => []),
        ]);
        if (info) setBrandInfo(info);
        setBrandAssets(assets);
        const logo = assets.find((a) => a.isLogo);
        if (logo) setLogoPreviewUrl(logo.url);
      } catch {
        // Ignore
      }
    };
    loadBrand();
  }, [workspaceId]);

  // Load templates when reaching step 4
  useEffect(() => {
    if (step !== 4 || !workspaceId) return;
    const loadTemplates = async () => {
      try {
        const tpls = await apiClient.get<TemplateSummary[]>(`/api/workspaces/${workspaceId}/templates`);
        setTemplates(tpls);
      } catch {
        // Ignore
      }
    };
    loadTemplates();
  }, [step, workspaceId]);

  // Load full template when selection changes
  useEffect(() => {
    if (!selectedTemplateId || !workspaceId) {
      setSelectedTemplate(null);
      return;
    }
    const loadTemplate = async () => {
      try {
        const tpl = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${selectedTemplateId}`);
        setSelectedTemplate(tpl);
      } catch {
        setSelectedTemplate(null);
      }
    };
    loadTemplate();
  }, [selectedTemplateId, workspaceId]);

  // Generate thumbnails when template or posts change
  useEffect(() => {
    if (!selectedTemplate || posts.length === 0) {
      setThumbnails({});
      return;
    }
    const generateThumbnails = async () => {
      const logoAsset = brandAssets.find((a) => a.isLogo);
      const newThumbnails: Record<string, string> = {};
      for (const post of posts) {
        if (!post.imageUrl) continue;
        try {
          const dataUrl = await renderCompositeThumbnail(
            post.imageUrl,
            selectedTemplate,
            400,
            {
              dynamicTexts: post.dynamicTexts
                ? Object.fromEntries(
                    Object.entries(post.dynamicTexts).map(([k, v]) => [k, v])
                  )
                : undefined,
              workspaceLogoUrl: logoAsset?.url,
            }
          );
          if (dataUrl) newThumbnails[post.id] = dataUrl;
        } catch {
          // Skip failed thumbnails
        }
      }
      setThumbnails(newThumbnails);
    };
    generateThumbnails();
  }, [selectedTemplate, posts, brandAssets]);

  // ─── Handlers ───

  const handleScrape = async () => {
    if (!scrapeUrl || !workspaceId) return;
    setIsScraping(true);
    setError('');
    try {
      const data = await apiClient.post<ScrapeResponse>(`/api/workspaces/${workspaceId}/scrape`, {
        url: scrapeUrl,
      });
      setBrandInfo(data);
      setScrapeUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze website');
    } finally {
      setIsScraping(false);
    }
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleUploadLogo = async () => {
    if (!logoFile || !workspaceId) return;
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      formData.append('name', 'Logo');
      const asset = await apiClient.upload<BrandAsset>(
        `/api/workspaces/${workspaceId}/assets/upload`,
        formData
      );
      // Set as logo
      await apiClient.post(`/api/workspaces/${workspaceId}/assets/${asset.id}/set-logo`, {});
      setBrandAssets((prev) => [...prev.map((a) => ({ ...a, isLogo: false })), { ...asset, isLogo: true }]);
      setLogoFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleContinueStep1 = async () => {
    // Upload logo if new file was selected
    if (logoFile) {
      await handleUploadLogo();
    }
    setStep(2);
  };

  const toggleNetwork = (network: SocialNetwork) => {
    setSelectedNetworks((prev) =>
      prev.includes(network)
        ? prev.filter((n) => n !== network)
        : [...prev, network]
    );
  };

  const totalPosts = selectedNetworks.length * postsPerNetwork;

  const handleGenerate = useCallback(async () => {
    if (totalPosts === 0 || !workspaceId) return;

    if (!user || user.credits < totalPosts) {
      setError(`Not enough credits. You need ${totalPosts} but have ${user?.credits ?? 0}.`);
      return;
    }

    setStep(3);
    setError('');

    try {
      const platformCounts = selectedNetworks.reduce(
        (acc, net) => ({ ...acc, [net]: postsPerNetwork }),
        {} as Record<string, number>
      );

      const response = await apiClient.post<GenerateResponse>(
        `/api/workspaces/${workspaceId}/generate`,
        {
          platformCounts,
          customPrompt: customPrompt || undefined,
          imageSourceConfig: {
            enabledSources: ['stock', 'pastel'],
            mix: { stock: 60, pastel: 40, asset: 0, ai: 0 },
            selectedAssetIds: [],
          },
        }
      );

      setPosts(response.posts);
      updateCredits(response.creditsRemaining);
      setStep(4);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate posts');
      setStep(2);
    }
  }, [workspaceId, selectedNetworks, postsPerNetwork, customPrompt, totalPosts, user, updateCredits]);

  const handleDownloadZip = useCallback(async (quality: ExportQuality = 'mobile') => {
    if (!selectedTemplate || posts.length === 0 || !workspaceId) return;

    const postsWithImages = posts.filter((p) => p.imageUrl);
    if (postsWithImages.length === 0) return;

    const preset = EXPORT_PRESETS[quality];

    setIsBulkProcessing(true);
    setBulkProgress({ current: 0, total: postsWithImages.length });

    const zip = new JSZip();
    const logoAsset = brandAssets.find((a) => a.isLogo);
    const imageFilenameByPostId: Record<string, string> = {};
    const imagePublicUrlByPostId: Record<string, string> = {};

    try {
      // 1. Render images with templates, add to ZIP, and upload to R2
      for (let i = 0; i < postsWithImages.length; i++) {
        const post = postsWithImages[i];
        setBulkProgress({ current: i + 1, total: postsWithImages.length });

        const dynamicTexts = post.dynamicTexts
          ? Object.fromEntries(
              Object.entries(post.dynamicTexts).map(([k, v]) => [k, v])
            )
          : undefined;

        const blob = await renderCompositeAsBlob(
          post.imageUrl!,
          selectedTemplate,
          { dynamicTexts, workspaceLogoUrl: logoAsset?.url, scale: preset.scale },
          preset.format,
          preset.quality
        );
        const filename = `${post.network}-${i + 1}.${preset.extension}`;
        if (blob) {
          zip.file(filename, blob);
          imageFilenameByPostId[post.id] = filename;

          // Upload composite to R2 so the CSV can reference a real public URL.
          // Falls back to the local filename if upload fails.
          try {
            const formData = new FormData();
            formData.append('file', blob, filename);
            const result = await apiClient.upload<{ url: string }>(
              `/api/workspaces/${workspaceId}/posts/${post.id}/upload-composite`,
              formData,
            );
            if (result?.url) {
              imagePublicUrlByPostId[post.id] = result.url;
            }
          } catch (uploadErr) {
            console.warn(`R2 upload failed for post ${post.id}, will use local filename in CSV:`, uploadErr);
          }
        }
      }

      // 2. Build CSV in social-media-posting format. Prefer public R2 URL, fall back to local filename.
      const csvEscape = (val: string): string => {
        if (!val) return '';
        if (val.includes(',') || val.includes('"') || val.includes('\n') || val.includes('\r')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const csvLines: string[] = [
        'postAtSpecificTime (YYYY-MM-DD HH:mm:ss),content,link (OGmetaUrl),imageUrls,gifUrl,videoUrls',
      ];

      for (const post of posts) {
        const content = csvEscape(post.content);
        const imageRef = imagePublicUrlByPostId[post.id] || imageFilenameByPostId[post.id] || '';
        csvLines.push(`,${content},,${csvEscape(imageRef)},,`);
      }

      zip.file('social-media-posting.csv', csvLines.join('\n'));

      // 3. Download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${workspace?.name || 'posts'}-export-${quality}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to create ZIP file');
    } finally {
      setIsBulkProcessing(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  }, [selectedTemplate, posts, workspaceId, workspace?.name, brandAssets]);

  const handleExport = handleDownloadZip;

  // ─── Render ───

  if (!workspace) {
    return <PageWrapper><LoadingWrapper><Spinner /><LoadingText>Loading...</LoadingText></LoadingWrapper></PageWrapper>;
  }

  return (
    <PageWrapper>
      {/* Stepper */}
      <StepperWrapper>
        {[1, 2, 3, 4].map((s, i) => (
          <span key={s} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StepDot $active={step === s} $completed={step > s}>
              {step > s ? '\u2713' : s}
            </StepDot>
            {i < 3 && <StepConnector $completed={step > s} />}
          </span>
        ))}
      </StepperWrapper>

      {/* Step 1: Brand Setup */}
      {step === 1 && (
        <>
          <StepTitle>Your Brand</StepTitle>
          <StepSubtitle>
            Let's learn about {workspace.name}. Enter your website and we'll do the rest.
          </StepSubtitle>

          <StepCard>
            {/* Show existing brand info banner */}
            {brandInfo && (
              <ExistingBrandBanner>
                <span style={{ fontSize: '20px' }}>&#x2705;</span>
                <span>
                  Brand info already loaded. You can re-analyze or continue to the next step.
                </span>
              </ExistingBrandBanner>
            )}

            {/* Scrape URL */}
            <InputWrapper>
              <Label>Website URL</Label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Input
                  value={scrapeUrl}
                  onChange={(e) => setScrapeUrl(e.target.value)}
                  placeholder="https://example.com"
                  style={{ flex: 1 }}
                  disabled={isScraping}
                />
                <Button
                  onClick={handleScrape}
                  disabled={!scrapeUrl || isScraping}
                >
                  {isScraping ? 'Analyzing...' : 'Analyze'}
                </Button>
              </div>
            </InputWrapper>

            {/* Brand Summary */}
            {brandInfo && (
              <BrandSummary>
                {brandInfo.description && (
                  <SummaryField>
                    <SummaryLabel>Description</SummaryLabel>
                    <SummaryValue>{brandInfo.description}</SummaryValue>
                  </SummaryField>
                )}
                {brandInfo.targetAudience && (
                  <SummaryField>
                    <SummaryLabel>Target Audience</SummaryLabel>
                    <SummaryValue>{brandInfo.targetAudience}</SummaryValue>
                  </SummaryField>
                )}
                {brandInfo.tone && (
                  <SummaryField>
                    <SummaryLabel>Brand Tone</SummaryLabel>
                    <SummaryValue>{brandInfo.tone}</SummaryValue>
                  </SummaryField>
                )}
                {brandInfo.keywords && brandInfo.keywords.length > 0 && (
                  <SummaryField>
                    <SummaryLabel>Keywords</SummaryLabel>
                    <SummaryValue>{brandInfo.keywords.join(', ')}</SummaryValue>
                  </SummaryField>
                )}
              </BrandSummary>
            )}

            {/* Logo Upload */}
            <div style={{ marginTop: '24px' }}>
              <Label>Brand Logo (optional)</Label>
              {logoPreviewUrl ? (
                <LogoPreview>
                  <img src={logoPreviewUrl} alt="Logo preview" />
                  <div>
                    <div style={{ fontWeight: 700 }}>{logoFile ? logoFile.name : 'Current logo'}</div>
                    <button
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreviewUrl(brandAssets.find((a) => a.isLogo)?.url || null);
                      }}
                      style={{ border: 'none', background: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '14px', padding: 0 }}
                    >
                      {logoFile ? 'Remove new file' : ''}
                    </button>
                  </div>
                </LogoPreview>
              ) : (
                <LogoUploadArea>
                  <input type="file" accept="image/*" onChange={handleLogoSelect} />
                  <span style={{ fontSize: '32px', marginBottom: '8px' }}>&#x1F4CE;</span>
                  <span>Click to upload your logo</span>
                </LogoUploadArea>
              )}
            </div>

            {error && <ErrorText>{error}</ErrorText>}

            <StepActions>
              <Button $variant="outline" onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
              <Button
                onClick={handleContinueStep1}
                disabled={isUploadingLogo}
              >
                {isUploadingLogo ? 'Uploading...' : 'Continue'}
              </Button>
            </StepActions>
          </StepCard>
        </>
      )}

      {/* Step 2: Configuration */}
      {step === 2 && (
        <>
          <StepTitle>What do you want to post?</StepTitle>
          <StepSubtitle>
            Select your social networks and how many posts you need.
          </StepSubtitle>

          <StepCard>
            <Label style={{ marginBottom: '12px', display: 'block' }}>Social Networks</Label>
            <NetworkGrid>
              {(Object.keys(SOCIAL_NETWORKS) as SocialNetwork[]).map((network) => (
                <NetworkCard
                  key={network}
                  $active={selectedNetworks.includes(network)}
                  onClick={() => toggleNetwork(network)}
                  type="button"
                >
                  <NetworkIcon>{NETWORK_ICONS[network]}</NetworkIcon>
                  {SOCIAL_NETWORKS[network].label}
                </NetworkCard>
              ))}
            </NetworkGrid>

            <CountRow>
              <CountLabel>Posts per network:</CountLabel>
              <CountInput
                type="number"
                min={1}
                max={20}
                value={postsPerNetwork}
                onChange={(e) => setPostsPerNetwork(Math.max(1, Math.min(20, Number(e.target.value))))}
              />
            </CountRow>

            {selectedNetworks.length > 0 && (
              <SelectedSummary>
                {selectedNetworks.length} network{selectedNetworks.length !== 1 ? 's' : ''} &times; {postsPerNetwork} posts = <strong>{totalPosts} posts total</strong> ({totalPosts} credits)
              </SelectedSummary>
            )}

            <div style={{ marginTop: '24px' }}>
              <InputWrapper>
                <Label>Special Instructions (optional)</Label>
                <TextArea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="E.g., Focus on summer promotions, use a friendly tone, mention our new service..."
                  rows={3}
                />
              </InputWrapper>
            </div>

            {error && <ErrorText>{error}</ErrorText>}

            <StepActions>
              <Button $variant="outline" onClick={() => { setStep(1); setError(''); }}>
                Back
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={selectedNetworks.length === 0 || totalPosts === 0}
              >
                Generate {totalPosts} Posts
              </Button>
            </StepActions>
          </StepCard>
        </>
      )}

      {/* Step 3: Generating */}
      {step === 3 && (
        <>
          <StepTitle>Creating your posts...</StepTitle>
          <StepSubtitle>
            Our AI is crafting {totalPosts} posts for {workspace.name}. This may take a moment.
          </StepSubtitle>

          <StepCard>
            <LoadingWrapper>
              <Spinner />
              <LoadingText>Generating content</LoadingText>
              <LoadingSubtext>
                Writing copy, finding images, and building your posts...
              </LoadingSubtext>
            </LoadingWrapper>
          </StepCard>
        </>
      )}

      {/* Step 4: Results */}
      {step === 4 && (
        <>
          <StepTitle>Your Posts are Ready!</StepTitle>
          <StepSubtitle>
            {posts.length} posts generated for {workspace.name}
          </StepSubtitle>

          <ResultsHeader>
            <TemplateRow>
              <Label>Apply Template:</Label>
              <TemplateSelect
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                <option value="">No template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.isGlobal ? '(Global)' : ''}
                  </option>
                ))}
              </TemplateSelect>
            </TemplateRow>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <TemplateSelect
                value={exportQuality}
                onChange={(e) => setExportQuality(e.target.value as ExportQuality)}
                title="Download size & quality"
              >
                <option value="mobile">{EXPORT_PRESETS.mobile.label}</option>
                <option value="low">{EXPORT_PRESETS.low.label}</option>
                <option value="original">{EXPORT_PRESETS.original.label}</option>
              </TemplateSelect>
              <Button $variant="outline" $size="sm" onClick={() => handleExport(exportQuality)}>
                Export CSV
              </Button>
              {selectedTemplate && (
                <Button
                  $size="sm"
                  onClick={() => handleDownloadZip(exportQuality)}
                  disabled={isBulkProcessing}
                >
                  {isBulkProcessing
                    ? `Processing ${bulkProgress.current}/${bulkProgress.total}...`
                    : 'Download ZIP'}
                </Button>
              )}
              <Button
                $variant="secondary"
                $size="sm"
                onClick={() => { setStep(2); setError(''); }}
              >
                Generate More
              </Button>
            </div>
          </ResultsHeader>

          {isBulkProcessing && (
            <ProgressBar>
              <ProgressFill $percent={bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0} />
            </ProgressBar>
          )}

          {error && <ErrorText>{error}</ErrorText>}

          {posts.length === 0 ? (
            <EmptyState>
              <p>No posts generated yet. Go back and try again.</p>
            </EmptyState>
          ) : (
            <PostsGrid>
              {posts.map((post) => (
                <PostCard key={post.id}>
                  <PostImage>
                    {thumbnails[post.id] ? (
                      <img src={thumbnails[post.id]} alt={`${post.network} post`} />
                    ) : post.imageUrl ? (
                      <img
                        src={post.imageUrl.startsWith('pastel://')
                          ? undefined
                          : post.imageUrl
                        }
                        alt={`${post.network} post`}
                        style={
                          post.imageUrl.startsWith('pastel://')
                            ? { backgroundColor: post.imageUrl.replace('pastel://', '') }
                            : undefined
                        }
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : null}
                    {post.imageUrl?.startsWith('pastel://') && !thumbnails[post.id] && (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: post.imageUrl.replace('pastel://', ''),
                      }} />
                    )}
                  </PostImage>
                  <PostBody>
                    <PostNetwork>{SOCIAL_NETWORKS[post.network]?.label || post.network}</PostNetwork>
                    <PostText>{post.content}</PostText>
                  </PostBody>
                </PostCard>
              ))}
            </PostsGrid>
          )}
        </>
      )}
    </PageWrapper>
  );
}
